import React, { useEffect } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import useJobProgressStore from '../stores/jobProgressStore';
import { getAdapter } from '../lib/jobAdapters';
import { isWarning } from '../lib/jobStatus';

const ACCENT_BAR = {
  amber: 'bg-amber-500 dark:bg-amber-400',
  sage: 'bg-sage-500 dark:bg-sage-400',
  rose: 'bg-rose-500 dark:bg-rose-400',
  indigo: 'bg-indigo-500 dark:bg-indigo-400',
};

const TERMINAL_OK_DISMISS_MS = 6000;

/** One minimized job's toast (mini progress + Open). Self-dismisses on success. */
const ToastRow = ({ job }) => {
  const expand = useJobProgressStore(s => s.expand);
  const dismiss = useJobProgressStore(s => s.dismiss);

  const adapter = getAdapter(job.kind);
  const { statusKind, phaseLabel, currentStep, percent } = adapter.normalize(job.raw, job.meta);
  const warn = isWarning(job.raw?.status);
  const barClass = ACCENT_BAR[job.accent] || ACCENT_BAR.indigo;

  // Auto-dismiss a clean success after a few seconds; keep failures/warnings/
  // paused until the user acts.
  useEffect(() => {
    if (statusKind === 'terminal-ok' && !warn) {
      const t = setTimeout(() => dismiss(job.id), TERMINAL_OK_DISMISS_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [statusKind, warn, job.id, dismiss]);

  const icon =
    statusKind === 'terminal-err' ? (
      <XCircleIcon className="w-4 h-4 text-red-500" />
    ) : statusKind === 'terminal-ok' ? (
      warn ? (
        <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
      ) : (
        <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
      )
    ) : statusKind === 'paused' ? (
      <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
    ) : (
      <ArrowPathIcon className="w-4 h-4 text-gray-500 animate-spin" />
    );

  const subline = statusKind === 'paused' ? 'Needs your input' : currentStep || phaseLabel;

  return (
    <div className="w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 animate-fade-in">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm font-medium text-gray-800 dark:text-gray-100 flex-1 truncate">{job.label}</span>
        <button
          type="button"
          onClick={() => expand(job.id)}
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          Open
        </button>
        <button
          type="button"
          onClick={() => dismiss(job.id)}
          aria-label="Dismiss"
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
        {percent === null ? (
          <div className={`${barClass} h-1.5 rounded-full w-full animate-pulse`} />
        ) : (
          <div
            className={`${barClass} h-1.5 rounded-full transition-all duration-300`}
            style={{ width: `${percent}%` }}
          />
        )}
      </div>
      {subline && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate" title={subline}>
          {subline}
        </p>
      )}
    </div>
  );
};

/** Fixed top-right stack of minimized/background job toasts. */
const JobProgressToast = ({ jobs }) => {
  if (!jobs || jobs.length === 0) return null;
  return (
    <div className="fixed top-20 right-4 z-50 space-y-2" role="status" aria-live="polite">
      {jobs.map(job => (
        <ToastRow key={job.id} job={job} />
      ))}
    </div>
  );
};

export default JobProgressToast;
